// Utility functions for calculating Signal AI scores

// Calculate individual scores (1-5 scale)
const calculateBinaryScore = (value, goodScore = 5, badScore = 1) => {
    return value === 0 ? goodScore : badScore;
};

const calculateThresholdScore = (value, thresholds) => {
    for (const [threshold, score] of thresholds) {
        if (value <= threshold) return score;
    }
    return thresholds[thresholds.length - 1][1];
};

const calculateGumScoreInternal = (recession, swelling) => {
    const average = (recession + swelling) / 2;
    return calculateThresholdScore(average, [[0, 5], [3, 4], [5, 3], [8, 2], [Infinity, 1]]);
};

// Helper function to get data from localStorage (for backward compatibility)
const getTeethHealth = () => {
    try {
        const data = JSON.parse(localStorage.getItem("tpscore")) || {};
        if (!data || Object.keys(data).length === 0) {
            return null; // Return null to indicate error state
        }
        return data;
    } catch (error) {
        console.error("Error parsing tpscore from localStorage:", error);
        return null;
    }
};

// Calculate individual component scores with parameters
export const calculateCavityScore = (teethHealth = null) => {
    const data = teethHealth || getTeethHealth();
    if (!data) return null; // Return null to indicate error state
    return calculateBinaryScore(data?.cavity_score);
};

export const calculateTartarScore = (teethHealth = null) => {
    const data = teethHealth || getTeethHealth();
    if (!data) return null; // Return null to indicate error state
    return calculateThresholdScore((data?.tartar_score || 0) / 10, [[3, 5], [6, 4], [10, 3], [Infinity, 1]]);
};

export const calculateGapScore = (teethHealth = null) => {
    const teethData = teethHealth || getTeethHealth();
    if (!teethData) return null; // Return null to indicate error state
    // Simplified logic: only check gaps_poorly_aligned_teeth from tpscore
    return (!teethData?.gaps_poorly_aligned_teeth) ? 5 : 2;
};

export const calculateGumScore = (teethHealth = null) => {
    const data = teethHealth || getTeethHealth();
    if (!data) return null; // Return null to indicate error state
    return calculateGumScoreInternal(
        (data?.gum_recession || 0) / 10,
        (data?.gum_swelling || 0) / 10
    );
};

// Calculate mean score out of 100 with parameters
export const calculateMeanScore = (teethHealth = null) => {
    const cavityScore = calculateCavityScore(teethHealth);
    const tartarScore = calculateTartarScore(teethHealth);
    const gapScore = calculateGapScore(teethHealth);
    const gumScore = calculateGumScore(teethHealth);

    // If any score is null, return null to indicate error state
    if (cavityScore === null || tartarScore === null || gapScore === null || gumScore === null) {
        return null;
    }

    // Calculate mean of 4 scores (each on 1-5 scale) and convert to 0-100
    const meanScore = (cavityScore + tartarScore + gapScore + gumScore) / 4;
    return Math.round((meanScore / 5) * 100);
};

// Get all scores as an object with parameters
export const getAllScores = (teethHealth = null) => {
    return {
        cavity: calculateCavityScore(teethHealth),
        tartar: calculateTartarScore(teethHealth),
        gap: calculateGapScore(teethHealth),
        gum: calculateGumScore(teethHealth),
        meanScore: calculateMeanScore(teethHealth)
    };
};
